import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Card, CardBody, Col, Container, Input, Label, Row } from "reactstrap";
import DataTableHelper from "../../../helpers/tables/DataTableHelper";
import Columns from "./Columns";
import DetailModal from "./Detail/DetailModal";
import { CONTEXTOS } from "./estados";
import {
  resourcesLogsWa,
  resourcesRoutes,
} from "../../../helpers/backend_helper";
import { getStorageUser } from "../../../helpers/api_helper";

const RESUMEN_VACIO = { total: 0, enviados: 0, fallidos: 0, pendientes: 0 };

const Tarjeta = ({ titulo, valor, color }) => (
  <Col md={3} sm={6} className="mb-2">
    <Card className="mb-0">
      <CardBody className="py-3">
        <small className="text-muted d-block">{titulo}</small>
        <h4 className={`mb-0 ${color}`}>{valor}</h4>
      </CardBody>
    </Card>
  </Col>
);

Tarjeta.propTypes = {
  titulo: PropTypes.string,
  valor: PropTypes.number,
  color: PropTypes.string,
};

/**
 * Bitacora de envios de WhatsApp. Solo la ve el Reseller (la ruta se cierra
 * con `policy: "routes"` en allRoutes.js y el backend vuelve a validarlo).
 */
const LogsWa = () => {
  const authUser = getStorageUser();
  const [pending, setPending] = useState(true);
  const [items, setItems] = useState([]);
  const [dataPag, setDataPag] = useState({ total: 0 });
  const [resumen, setResumen] = useState(RESUMEN_VACIO);
  const [modal, setModal] = useState({ show: false, item: null });
  const [filters, setFilters] = useState({
    page: 1,
    perPage: 20,
    search: "",
    // Al reves que los catalogos: aqui lo reciente es lo relevante.
    orderBy: "created_at",
    order: "desc",
    date_from: "",
    date_to: "",
    success: "",
    route_id: "",
    context: "",
    companySession: authUser.company_id,
  });
  const [routes, setRoutes] = useState([]);

  // Sin `page`, el backend devuelve todas las rutas de la empresa del Reseller.
  useEffect(() => {
    resourcesRoutes("get", {}).then((response) => {
      setRoutes(response?.data ?? []);
    });
  }, []);

  useEffect(() => {
    const getLogsWa = async () => {
      setPending(true);
      const response = await resourcesLogsWa("get", { params: filters });
      // Si el backend responde con la guarda de permisos, `data` no viene.
      setItems(response?.data ?? []);
      setDataPag(response?.meta ?? { total: 0 });
      setResumen(response?.resumen ?? RESUMEN_VACIO);
      setPending(false);
    };
    getLogsWa();
  }, [filters]);

  const detail = (row) => {
    setModal({ show: true, item: row });
  };

  const columns = Columns(detail);
  document.title = "WA enviados | Sistema de logística";

  // Las filas anteriores a esta pantalla no tienen empresa asignada, asi que
  // el filtro por company_id las deja fuera. Sin este aviso, el primer dia la
  // pantalla parece rota.
  const sinFiltros =
    !filters.search &&
    !filters.date_from &&
    !filters.date_to &&
    !filters.success &&
    !filters.route_id &&
    !filters.context;
  const mostrarAvisoHistorico = !pending && resumen.total === 0 && sinFiltros;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="col-12">
            <Row>
              <Tarjeta titulo="Total" valor={resumen.total} color="" />
              <Tarjeta
                titulo="Aceptados"
                valor={resumen.enviados}
                color="text-success"
              />
              <Tarjeta
                titulo="Fallidos"
                valor={resumen.fallidos}
                color="text-danger"
              />
              <Tarjeta
                titulo="Pendientes"
                valor={resumen.pendientes}
                color="text-warning"
              />
            </Row>
            <p className="text-muted mb-3">
              <small>
                «Aceptado» significa que Twilio recibió el mensaje; no garantiza
                la entrega final al destinatario.
              </small>
            </p>

            {mostrarAvisoHistorico && (
              <div className="alert alert-info">
                Los envíos anteriores a la puesta en marcha de esta pantalla no
                tienen empresa asignada y no se muestran. Los registros nuevos
                aparecerán a partir del siguiente reporte diario.
              </div>
            )}

            <DataTableHelper
              menuParent="Soporte"
              tittle="WA enviados"
              filterInp={true}
              columns={columns}
              items={items}
              pending={pending}
              config={dataPag}
              filters={filters}
              setFilters={setFilters}
            >
              <div className="col-sm-2">
                <Label className="mb-0">
                  <small className="text-muted">Desde</small>
                </Label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      date_from: e.target.value,
                      page: 1,
                    })
                  }
                />
              </div>
              <div className="col-sm-2">
                <Label className="mb-0">
                  <small className="text-muted">Hasta</small>
                </Label>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) =>
                    setFilters({ ...filters, date_to: e.target.value, page: 1 })
                  }
                />
              </div>
              <div className="col-sm-2">
                <Label className="mb-0">
                  <small className="text-muted">Estado</small>
                </Label>
                <Input
                  type="select"
                  value={filters.success}
                  onChange={(e) =>
                    setFilters({ ...filters, success: e.target.value, page: 1 })
                  }
                >
                  <option value="">Todos</option>
                  <option value="1">Aceptados</option>
                  <option value="0">Fallidos</option>
                  <option value="null">Pendientes</option>
                </Input>
              </div>
              <div className="col-sm-2">
                <Label className="mb-0">
                  <small className="text-muted">Ruta</small>
                </Label>
                <Input
                  type="select"
                  value={filters.route_id}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      route_id: e.target.value,
                      page: 1,
                    })
                  }
                >
                  <option value="">Todas</option>
                  {routes.map((route) => (
                    <option key={route.id} value={route.id}>
                      {route.name}
                    </option>
                  ))}
                </Input>
              </div>
              <div className="col-sm-2">
                <Label className="mb-0">
                  <small className="text-muted">Tipo de reporte</small>
                </Label>
                <Input
                  type="select"
                  value={filters.context}
                  onChange={(e) =>
                    setFilters({ ...filters, context: e.target.value, page: 1 })
                  }
                >
                  <option value="">Todos</option>
                  {Object.entries(CONTEXTOS).map(([clave, etiqueta]) => (
                    <option key={clave} value={clave}>
                      {etiqueta}
                    </option>
                  ))}
                </Input>
              </div>
            </DataTableHelper>
          </div>
        </Container>

        {modal.show && <DetailModal modal={modal} setModal={setModal} />}
      </div>
    </React.Fragment>
  );
};

export default LogsWa;
